import React, { useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Card, Button, Row, Col, Form, Alert, Spinner } from 'react-bootstrap';
import { FiArrowLeft, FiBriefcase, FiSave, FiCheckCircle, FiFolder } from 'react-icons/fi';
import { useDispatch, useSelector } from 'react-redux';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { updatePartnerRequest, resetUpdatePartner } from '../../store/actions/partnerActions';
import 'bootstrap/dist/css/bootstrap.css';
import { getPartnerById } from '../../services/api';
import { handleApiError } from '../../utils/errorHandler';
import './AdminPartnerEdit.css';
import './AdminPartner.css';

const decodeS3Link = (link) => {
    if (!link || typeof link !== 'string') return link;
    try {
        return decodeURIComponent(link);
    } catch {
        return link;
    }
};

const PartnerUpdateSuccessResult = ({ result, onDismiss }) => {
    const documents = Array.isArray(result?.documents) ? result.documents : [];
    const s3Link = decodeS3Link(result?.s3_link);

    return (
        <Card className="partner-create-success mb-3">
            <Card.Body>
                <div className="d-flex align-items-start gap-3 mb-3">
                    <FiCheckCircle size={28} className="text-success flex-shrink-0 mt-1" aria-hidden />
                    <div className="flex-grow-1">
                        <h3 className="h5 mb-2">Partenaire mis à jour avec succès</h3>
                        {result?.message && (
                            <div className="partner-create-success__message text-break">
                                {result.message}
                            </div>
                        )}
                    </div>
                    <Button variant="outline-secondary" size="sm" onClick={onDismiss}>
                        Fermer
                    </Button>
                </div>

                {result?.warning && (
                    <Alert variant="warning" className="mb-3">
                        <div className="partner-create-success__message text-break mb-0">
                            {result.warning}
                        </div>
                    </Alert>
                )}

                {(s3Link || documents.length > 0) && (
                    <div className="partner-create-success__s3 border rounded p-3">
                        <div className="d-flex align-items-center gap-2 mb-2">
                            <FiFolder aria-hidden />
                            <h4 className="h6 mb-0">Dossier S3 — documents contractuels</h4>
                        </div>
                        {s3Link && (
                            <p className="small mb-2 font-monospace text-break">
                                <strong>Lien S3 :</strong> {s3Link}
                            </p>
                        )}
                        {documents.length > 0 && (
                            <>
                                <div className="small fw-semibold mb-1">Documents à déposer :</div>
                                <ul className="small mb-0 ps-3">
                                    {documents.map((doc) => (
                                        <li key={doc}><code>{doc}</code></li>
                                    ))}
                                </ul>
                            </>
                        )}
                    </div>
                )}
            </Card.Body>
        </Card>
    );
};

// Schema de validation avec Yup
const partnerSchema = yup.object().shape({
    email: yup
        .string()
        .email('L\'email doit être valide'),
    company_name: yup
        .string()
        .required('Le nom de l\'entreprise est requis')
        .min(2, 'Le nom de l\'entreprise doit contenir au moins 2 caractères'),
    first_name: yup
        .string()
        .min(2, 'Le prénom doit contenir au moins 2 caractères'),
    last_name: yup
        .string()
        .min(2, 'Le nom doit contenir au moins 2 caractères'),
    phone_number: yup
        .string()
        .matches(/^[0-9+\-\s()]*$/, 'Le numéro de téléphone n\'est pas valide'),
    commission: yup
        .number()
        .min(0, 'La commission doit être positive ou nulle')
        .typeError('La commission doit être un nombre'),
    city: yup
        .string(),
    country: yup
        .string(),
    user_address: yup
        .string(),
    zip_code: yup
        .string(),
    country_code: yup
        .string()
        .max(2, 'Le code pays doit contenir au maximum 2 caractères'),
    legal_info: yup
        .string(),
    logo_url: yup
        .string()
        .nullable()
        .transform((value, originalValue) => (originalValue === '' ? null : value))
        .max(2048, 'L\'URL du logo ne peut pas dépasser 2048 caractères')
        .test('is-url', 'L\'URL du logo n\'est pas valide', (value) => {
            if (!value) return true;
            try {
                // eslint-disable-next-line no-new
                new URL(value);
                return true;
            } catch {
                return false;
            }
        }),
    contract_doc_s3_link: yup
        .string()
        .nullable()
        .transform((value, originalValue) => (originalValue === '' ? null : value))
        .max(2048, 'Le lien S3 ne peut pas dépasser 2048 caractères'),
    role: yup
        .string()
        .required(),
});

const AdminPartnerEdit = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { partnerId } = useParams();
    const { update } = useSelector((state) => state.partners);

    const {
        register,
        handleSubmit,
        formState: { errors, isSubmitting },
        reset,
        setValue,
    } = useForm({
        resolver: yupResolver(partnerSchema),
        defaultValues: {
            email: '',
            city: '',
            country: '',
            user_address: '',
            zip_code: '',
            first_name: '',
            last_name: '',
            phone_number: '',
            company_name: '',
            country_code: '',
            commission: 0,
            legal_info: '',
            logo_url: '',
            contract_doc_s3_link: '',
            role: 'Partner',
        },
    });

    useEffect(() => {
        dispatch(resetUpdatePartner());
    }, [dispatch, partnerId]);

    useEffect(() => {
        const fetchPartner = async () => {
            try {
                const data = await getPartnerById(partnerId);
                const partnerData = data?.user || data?.partner || data?.data || data || null;
                if (partnerData) {
                    reset({
                        email: partnerData.email || '',
                        city: partnerData.city || '',
                        country: partnerData.country || '',
                        user_address: partnerData.user_address || partnerData.address || '',
                        zip_code: partnerData.zip_code || '',
                        first_name: partnerData.first_name || '',
                        last_name: partnerData.last_name || '',
                        phone_number: partnerData.phone_number || partnerData.phone || '',
                        company_name: partnerData.company_name || '',
                        country_code: partnerData.country_code || partnerData.partner_country_code || '',
                        commission: partnerData.commission ? parseFloat(partnerData.commission) : 0,
                        legal_info: partnerData.legal_info || '',
                        logo_url: partnerData.logo_url || '',
                        contract_doc_s3_link: partnerData.contract_doc_s3_link || '',
                        role: partnerData.role || 'Partner',
                    });
                }
            } catch (e) {
                console.error('Erreur lors du chargement du partenaire:', handleApiError(e, 'Erreur lors du chargement du partenaire'));
            }
        };
        if (partnerId) fetchPartner();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [partnerId, reset]);

    useEffect(() => {
        if (update?.success && update?.data?.user) {
            const partnerData = update.data.user;
            reset({
                email: partnerData.email || '',
                city: partnerData.city || '',
                country: partnerData.country || '',
                user_address: partnerData.user_address || partnerData.address || '',
                zip_code: partnerData.zip_code || '',
                first_name: partnerData.first_name || '',
                last_name: partnerData.last_name || '',
                phone_number: partnerData.phone_number || partnerData.phone || '',
                company_name: partnerData.company_name || '',
                country_code: partnerData.country_code || partnerData.partner_country_code || '',
                commission: partnerData.commission != null ? parseFloat(partnerData.commission) : 0,
                legal_info: partnerData.legal_info || '',
                logo_url: partnerData.logo_url || '',
                contract_doc_s3_link: partnerData.contract_doc_s3_link || '',
                role: partnerData.role || 'Partner',
            });
        }
    }, [update?.success, update?.data, reset]);

    const onSubmit = (data) => {
        dispatch(updatePartnerRequest(partnerId, {
            ...data,
            logo_url: data.logo_url?.trim() || null,
            contract_doc_s3_link: data.contract_doc_s3_link?.trim() || null,
        }));
    };

    const handleDismissSuccess = () => {
        dispatch(resetUpdatePartner());
    };

    return (
        <div className="dashboard-page admin-partner-edit-page">
            <div className="d-flex justify-content-between align-items-center mb-3">
                <h2 className="page-title">
                    <FiBriefcase className="me-2" />
                    Modifier le partenaire
                </h2>
                <Button variant="outline-secondary" onClick={() => navigate(-1)}>
                    <FiArrowLeft className="me-2" /> Retour
                </Button>
            </div>

            {update?.error && <Alert variant="danger" className="mb-3" dismissible onClose={() => dispatch(resetUpdatePartner())}>{update.error}</Alert>}
            {update?.success && update?.data && (
                <PartnerUpdateSuccessResult
                    result={update.data}
                    onDismiss={handleDismissSuccess}
                />
            )}

            <Card>
                <Card.Body>
                    <Form onSubmit={handleSubmit(onSubmit)}>
                        <Row>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Email</Form.Label>
                                    <Form.Control
                                        type="email"
                                        {...register('email')}
                                        isInvalid={!!errors.email}
                                    />
                                    {errors.email && (
                                        <Form.Control.Feedback type="invalid">
                                            {errors.email.message}
                                        </Form.Control.Feedback>
                                    )}
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Nom de l'entreprise *</Form.Label>
                                    <Form.Control
                                        type="text"
                                        {...register('company_name')}
                                        isInvalid={!!errors.company_name}
                                    />
                                    {errors.company_name && (
                                        <Form.Control.Feedback type="invalid">
                                            {errors.company_name.message}
                                        </Form.Control.Feedback>
                                    )}
                                </Form.Group>
                            </Col>
                        </Row>
                        <Row>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Prénom</Form.Label>
                                    <Form.Control
                                        type="text"
                                        {...register('first_name')}
                                        isInvalid={!!errors.first_name}
                                    />
                                    {errors.first_name && (
                                        <Form.Control.Feedback type="invalid">
                                            {errors.first_name.message}
                                        </Form.Control.Feedback>
                                    )}
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Nom</Form.Label>
                                    <Form.Control
                                        type="text"
                                        {...register('last_name')}
                                        isInvalid={!!errors.last_name}
                                    />
                                    {errors.last_name && (
                                        <Form.Control.Feedback type="invalid">
                                            {errors.last_name.message}
                                        </Form.Control.Feedback>
                                    )}
                                </Form.Group>
                            </Col>
                        </Row>
                        <Row>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Téléphone</Form.Label>
                                    <Form.Control
                                        type="tel"
                                        {...register('phone_number')}
                                        isInvalid={!!errors.phone_number}
                                    />
                                    {errors.phone_number && (
                                        <Form.Control.Feedback type="invalid">
                                            {errors.phone_number.message}
                                        </Form.Control.Feedback>
                                    )}
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Commission</Form.Label>
                                    <Form.Control
                                        type="number"
                                        step="0.01"
                                        min="0"
                                        {...register('commission')}
                                        isInvalid={!!errors.commission}
                                    />
                                    {errors.commission && (
                                        <Form.Control.Feedback type="invalid">
                                            {errors.commission.message}
                                        </Form.Control.Feedback>
                                    )}
                                </Form.Group>
                            </Col>
                        </Row>
                        <Form.Group className="mb-3">
                            <Form.Label>Adresse</Form.Label>
                            <Form.Control
                                as="textarea"
                                rows={2}
                                {...register('user_address')}
                                isInvalid={!!errors.user_address}
                            />
                            {errors.user_address && (
                                <Form.Control.Feedback type="invalid">
                                    {errors.user_address.message}
                                </Form.Control.Feedback>
                            )}
                        </Form.Group>
                        <Row>
                            <Col md={4}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Ville</Form.Label>
                                    <Form.Control
                                        type="text"
                                        {...register('city')}
                                        isInvalid={!!errors.city}
                                    />
                                    {errors.city && (
                                        <Form.Control.Feedback type="invalid">
                                            {errors.city.message}
                                        </Form.Control.Feedback>
                                    )}
                                </Form.Group>
                            </Col>
                            <Col md={4}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Code postal</Form.Label>
                                    <Form.Control
                                        type="text"
                                        {...register('zip_code')}
                                        isInvalid={!!errors.zip_code}
                                    />
                                    {errors.zip_code && (
                                        <Form.Control.Feedback type="invalid">
                                            {errors.zip_code.message}
                                        </Form.Control.Feedback>
                                    )}
                                </Form.Group>
                            </Col>
                            <Col md={4}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Code pays</Form.Label>
                                    <Form.Control
                                        type="text"
                                        maxLength={2}
                                        placeholder="Ex: KZ"
                                        {...register('country_code')}
                                        isInvalid={!!errors.country_code}
                                    />
                                    {errors.country_code && (
                                        <Form.Control.Feedback type="invalid">
                                            {errors.country_code.message}
                                        </Form.Control.Feedback>
                                    )}
                                </Form.Group>
                            </Col>
                        </Row>
                        <Row>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Pays</Form.Label>
                                    <Form.Control
                                        type="text"
                                        {...register('country')}
                                        isInvalid={!!errors.country}
                                    />
                                    {errors.country && (
                                        <Form.Control.Feedback type="invalid">
                                            {errors.country.message}
                                        </Form.Control.Feedback>
                                    )}
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Rôle</Form.Label>
                                    <Form.Control
                                        type="text"
                                        {...register('role')}
                                        disabled
                                    />
                                </Form.Group>
                            </Col>
                        </Row>
                        <Form.Group className="mb-3">
                            <Form.Label>URL du logo</Form.Label>
                            <Form.Control
                                type="url"
                                maxLength={2048}
                                placeholder="https://exemple.com/logo.png (optionnel)"
                                {...register('logo_url')}
                                isInvalid={!!errors.logo_url}
                            />
                            {errors.logo_url && (
                                <Form.Control.Feedback type="invalid">
                                    {errors.logo_url.message}
                                </Form.Control.Feedback>
                            )}
                            <Form.Text className="text-muted">
                                Optionnel. URL du logo partenaire (max. 2048 caractères).
                            </Form.Text>
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>Lien S3 documentation contractuelle</Form.Label>
                            <Form.Control
                                type="text"
                                maxLength={2048}
                                placeholder="s3://bucket/chemin/vers/dossier (optionnel)"
                                {...register('contract_doc_s3_link')}
                                isInvalid={!!errors.contract_doc_s3_link}
                            />
                            {errors.contract_doc_s3_link && (
                                <Form.Control.Feedback type="invalid">
                                    {errors.contract_doc_s3_link.message}
                                </Form.Control.Feedback>
                            )}
                            <Form.Text className="text-muted">
                                Optionnel. Chemin S3 des documents contractuels du partenaire.
                            </Form.Text>
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>Informations légales</Form.Label>
                            <Form.Control
                                as="textarea"
                                rows={3}
                                {...register('legal_info')}
                                isInvalid={!!errors.legal_info}
                            />
                            {errors.legal_info && (
                                <Form.Control.Feedback type="invalid">
                                    {errors.legal_info.message}
                                </Form.Control.Feedback>
                            )}
                        </Form.Group>
                        <div className="d-flex justify-content-end gap-2">
                            <Button variant="secondary" onClick={() => navigate(-1)} disabled={update?.loading || isSubmitting}>
                                Annuler
                            </Button>
                            {update?.success && (
                                <Button variant="info" onClick={() => navigate(`/admin/partner/${partnerId}`)}>
                                    Voir les détails
                                </Button>
                            )}
                            <Button variant="primary" type="submit" disabled={update?.loading || isSubmitting}>
                                {update?.loading || isSubmitting ? (
                                    <>
                                        <Spinner size="sm" className="me-2" />
                                        Enregistrement...
                                    </>
                                ) : (
                                    <>
                                        <FiSave className="me-2" />
                                        Enregistrer
                                    </>
                                )}
                            </Button>
                        </div>
                    </Form>
                </Card.Body>
            </Card>
        </div>
    );
};

export default AdminPartnerEdit;
